import { memo, useRef, useState } from 'react';
import { TruncatedTooltip } from '@/components/ui/TruncatedTooltip';
import { Badge } from '@/components/ui/badge';
import { ImagePlaceholder } from '@/components/ui/ImagePlaceholder';
import { SelectionCheckbox } from '@/components/ui/SelectionCheckbox';
import { cn } from '@/lib/utils';
import { UserItem, ItemVisibility } from '@/types/userItem';
import { Loader2, ImagePlus, Camera } from 'lucide-react';
import { QuickPriceEditDialog } from '@/components/inventory/QuickPriceEditDialog';
import { QuickListPopover } from '@/components/inventory/QuickListPopover';
import { Tag } from 'lucide-react';
import { ImageSelectionDialog } from './ImageSelectionDialog';
import { useLongPress } from '@/hooks/useLongPress';
import { useDropToUploadPhotos } from '@/hooks/useDropToUploadPhotos';
import { ItemMetaBadges } from '@/components/shared/ItemMetaBadges';
import { transformedImageUrl } from '@/lib/imageTransform';
import { useHasActiveEbayListing } from '@/hooks/useMyActiveEbayItems';
import { EbayBag } from '@/components/icons/EbayBag';
import { isPendingSale } from '@/components/inventory/inventoryHelpers';
import { formatUsdCents } from '@/lib/formatMoney';
import { InlinePriceEditor } from '@/components/inventory/InlinePriceEditor';
import { useUIStore } from '@/store/uiStore';

interface CollectionItemCardProps {
  item: UserItem;
  onVisibilityChange: (itemId: string, visibility: ItemVisibility) => void;
  onDelete: (itemId: string) => void;
  isUpdating?: boolean;
  interestCount?: number;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (itemId: string, opts?: { shift?: boolean }) => void;
  onEnterSelectionMode?: () => void;
  /** When true, mark the image as eager + high-priority (first-viewport LCP row). */
  priority?: boolean;
}

// Standard sizes hint for the responsive Inventory grid.
// Mobile 2-col → ~50vw; sm 3-col → 33vw; md 4-col → 25vw; xl 5-col → 20vw.
const TILE_SIZES = '(min-width: 1280px) 20vw, (min-width: 768px) 25vw, (min-width: 640px) 33vw, 50vw';

function CollectionItemCardImpl({
  item,
  isUpdating = false,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelection,
  onEnterSelectionMode,
  priority = false,
}: CollectionItemCardProps) {
  const [priceEditOpen, setPriceEditOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const isOnEbay = useHasActiveEbayListing(item.id);
  const setPendingSalesQueueOpen = useUIStore((s) => s.setPendingSalesQueueOpen);

  // Track when a long-press fires so the follow-up click on the same touch
  // doesn't open the item dialog before selection mode has rendered.
  const longPressAtRef = useRef<number | null>(null);
  const isAfterLongPress = () => {
    if (!longPressAtRef.current) return false;
    if (Date.now() - longPressAtRef.current > 500) {
      longPressAtRef.current = null;
      return false;
    }
    return true;
  };

  // Long-press → selection mode
  const longPress = useLongPress(
    () => {
      if (isSelectionMode) return;
      longPressAtRef.current = Date.now();
      onEnterSelectionMode?.();
      onToggleSelection?.(item.id);
    },
    { disabled: isSelectionMode || !onEnterSelectionMode }
  );

  // Drag-and-drop photo upload (desktop)
  const existingPhotoCount = item.images?.length ?? 0;
  const { isDragOver, isUploading, dropHandlers } = useDropToUploadPhotos({
    itemId: item.id,
    existingCount: existingPhotoCount,
    disabled: isSelectionMode,
  });

  const hasUserImage = !!item.images?.[0]?.image_url;
  const hasStockImage = !!item.stock_image_url;
  const displayImage = hasUserImage
    ? item.images![0].image_url
    : item.stock_image_url || null;
  const isStockImage = !hasUserImage && hasStockImage;
  const isSold = !!item.sold_at;

  const priceDisplay = item.asking_price_cents
    ? (item.asking_price_cents / 100).toFixed(2)
    : null;
  const soldPriceDisplay = item.sold_price_cents != null
    ? formatUsdCents(item.sold_price_cents)
    : null;

  const handleImageClick = () => {
    if (isSelectionMode) return;
    if (isAfterLongPress()) return;
    if (!displayImage) setImageDialogOpen(true);
    else setPriceEditOpen(true);
  };

  const handleContentClick = () => {
    if (isSelectionMode) return;
    if (isAfterLongPress()) return;
    setPriceEditOpen(true);
  };

  const handleRootClick = (e: React.MouseEvent) => {
    if (isAfterLongPress()) return;
    if (isSelectionMode) onToggleSelection?.(item.id, { shift: e.shiftKey });
  };

  const isUnlisted = item.visibility !== 'listed' && !isSold && !isPendingSale(item);
  const dimText = isUnlisted && !isSelectionMode;

  return (
    <>
      <div
        data-inventory-card
        className={cn(
          'group flex flex-col relative select-none transition-opacity',
          (isUpdating || isUploading) && 'opacity-50 pointer-events-none',
          isSelectionMode && 'cursor-pointer'
        )}
        onClick={isSelectionMode ? handleRootClick : undefined}
        {...longPress.handlers}
        {...dropHandlers}
        style={{ touchAction: 'pan-y', WebkitTouchCallout: 'none' }}
      >
        {/* Image */}
        <div
          onClick={handleImageClick}
          className={cn(
            'relative aspect-square w-full overflow-hidden rounded-xl bg-muted/30 mb-3 cursor-pointer',
            isSelectionMode && isSelected && 'ring-2 ring-inset ring-active-gold',
            isSelectionMode && !isSelected && 'ring-1 ring-inset ring-white/10',
            isDragOver && 'ring-2 ring-inset ring-active-gold'
          )}
        >
          {displayImage ? (
            <img
              src={transformedImageUrl(displayImage, { width: 400, height: 400, quality: 70, resize: 'cover' }) || displayImage}
              alt={item.title}
              width={400}
              height={400}
              sizes={TILE_SIZES}
              className="h-full w-full object-cover opacity-0 transition-[opacity,transform] duration-300 group-hover:scale-[1.03] [&.is-loaded]:opacity-100"
              loading={priority ? 'eager' : 'lazy'}
              decoding={priority ? 'sync' : 'async'}
              {...(priority ? { fetchpriority: 'high' as const } : {})}
              onLoad={(e) => e.currentTarget.classList.add('is-loaded')}
              onError={(e) => {
                const img = e.currentTarget;
                img.classList.add('is-loaded');
                if (img.src !== displayImage) img.src = displayImage;
              }}
            />
          ) : (
            <ImagePlaceholder variant="card" interactive className="w-full h-full rounded-none" />
          )}

          {/* Unlisted image dim — List button stays full opacity */}
          {isUnlisted && !isSelectionMode && (
            <div className="absolute inset-0 z-[5] bg-black/60 group-hover:bg-transparent transition-colors pointer-events-none" />
          )}

          {/* Drag-over overlay */}
          {(isDragOver || isUploading) && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-1.5 bg-active-gold/15 backdrop-blur-[2px] pointer-events-none">
              {isUploading ? (
                <Loader2 className="w-6 h-6 text-active-gold animate-spin" />
              ) : (
                <ImagePlus className="w-6 h-6 text-active-gold" />
              )}
              <span className="text-[11px] font-medium text-active-gold">
                {isUploading ? 'Uploading…' : 'Drop to add photo'}
              </span>
            </div>
          )}

          {/* Selection check pill */}
          {isSelectionMode && (
            <div className="absolute top-2 right-2 z-20 pointer-events-none" aria-hidden="true">
              <SelectionCheckbox
                variant="pill"
                checked={isSelected}
                className="pointer-events-none"
              />
            </div>
          )}

          {/* Pending eBay sale ribbon */}
          {isPendingSale(item) && !isSelectionMode && (
            <div className="absolute top-1.5 right-1.5 z-20 pointer-events-none">
              <span className="inline-flex items-center gap-1 rounded-full bg-active-gold px-2 py-0.5 text-[10px] font-semibold text-black shadow-[0_2px_6px_rgba(0,0,0,0.5)]">
                Pending sale
              </span>
            </div>
          )}

          {/* Sold ribbon */}
          {isSold && !isSelectionMode && (
            <div className="absolute top-1.5 right-1.5 z-20 pointer-events-none">
              <span className="inline-flex items-center gap-1 rounded-full bg-foreground px-2 py-0.5 text-[10px] font-semibold text-background">
                Sold
              </span>
            </div>
          )}

          {/* Status glyphs — gold tag (listed on CardSimp, top) + eBay bag (bottom), stacked top-right */}
          {!isSelectionMode && !isPendingSale(item) && !isSold && (item.visibility === 'listed' || isOnEbay) && (
            <div className="absolute top-1.5 right-1.5 z-10 flex flex-col items-center gap-1">
              {item.visibility === 'listed' && (
                <div
                  className="flex items-center justify-center h-5 w-5 rounded-full bg-active-gold/90 shadow-[0_2px_6px_rgba(0,0,0,0.5)]"
                  title="Listed on CardSimp"
                  aria-label="Listed on CardSimp"
                >
                  <Tag className="h-3 w-3 text-black" strokeWidth={2.5} />
                </div>
              )}
              {isOnEbay && (
                <div
                  className="flex items-center justify-center h-5 w-5 rounded-full bg-white shadow-[0_2px_6px_rgba(0,0,0,0.5)]"
                  title="Listed on eBay"
                  aria-label="Listed on eBay"
                >
                  <EbayBag className="h-3 w-3" />
                </div>
              )}
            </div>
          )}


          {/* Quantity badge */}
          {item.quantity > 1 && !isSelectionMode && (
            <div className="absolute top-1.5 left-1.5 z-10">
              <Badge className="bg-black/70 text-white border-0 text-xs px-1.5 py-0.5">
                x{item.quantity}
              </Badge>
            </div>
          )}

          {/* Sample photo indicator — subtle camera glyph, no text */}
          {isStockImage && (
            <div
              className="absolute bottom-1.5 left-1.5 z-10 flex items-center justify-center h-5 w-5 rounded-full bg-black/60 backdrop-blur-sm pointer-events-none"
              title="Stock photo"
              aria-label="Stock photo"
            >
              <Camera className="h-3 w-3 text-white/80" />
            </div>
          )}
        </div>

        {/* Text block */}
        <div className="px-1 flex flex-col gap-1">
          {/* Title + Card Number */}
          <div
            className={cn('flex items-baseline justify-between gap-2 cursor-pointer', dimText && 'opacity-60')}
            onClick={handleContentClick}
          >
            <TruncatedTooltip text={item.title}>
              <h3 className="text-[15px] font-semibold text-foreground leading-tight truncate cursor-default flex-1 min-w-0">
                {item.title}
              </h3>
            </TruncatedTooltip>
            {item.card_number && (
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground/70 shrink-0 tabular-nums">
                #{item.card_number}
              </span>
            )}
          </div>

          {/* Price row */}
          <div className="flex items-center justify-between gap-2 mt-1">
            <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
              {isPendingSale(item) && !isSelectionMode ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPendingSalesQueueOpen(true);
                  }}
                  className="inline-flex items-center gap-1.5 max-w-full rounded-full border border-active-gold/40 bg-active-gold/[0.08] px-2 py-1 text-[11px] font-semibold text-active-gold hover:bg-active-gold/15 transition-colors"
                >
                  <span className="truncate">Pending sale</span>
                  {item.pending_sale_price_cents != null && (
                    <span className="tabular-nums shrink-0">
                      {formatUsdCents(item.pending_sale_price_cents)}
                    </span>
                  )}
                </button>
              ) : isSold ? (
                <span className={cn('text-[16px] font-bold text-muted-foreground tabular-nums truncate', dimText && 'opacity-60')}>
                  {soldPriceDisplay ? `Sold ${soldPriceDisplay}` : 'Sold'}
                </span>
              ) : isSelectionMode ? (
                priceDisplay ? (
                  <span className={cn('text-[16px] font-bold text-active-gold tabular-nums truncate', dimText && 'opacity-60')}>
                    ${priceDisplay}
                  </span>
                ) : item.is_negotiable && item.visibility === 'listed' ? (
                  <span className={cn('text-[16px] font-bold text-active-gold whitespace-nowrap', dimText && 'opacity-60')}>
                    Taking Offers
                  </span>
                ) : item.visibility === 'listed' ? (
                  <span className={cn('text-xs text-muted-foreground', dimText && 'opacity-60')}>No Price</span>
                ) : null
              ) : (
                <InlinePriceEditor item={item} className="relative z-20 min-w-[4.25rem] -ml-1 px-1.5 py-1">
                  {priceDisplay ? (
                    <span className={cn('text-[16px] font-bold text-active-gold tabular-nums truncate', dimText && 'opacity-60')}>
                      ${priceDisplay}
                    </span>
                  ) : item.is_negotiable && item.visibility === 'listed' ? (
                    <span className={cn('text-[16px] font-bold text-active-gold whitespace-nowrap', dimText && 'opacity-60')}>
                      Taking Offers
                    </span>
                  ) : (
                    <span className={cn('text-xs text-muted-foreground', dimText && 'opacity-60')}>
                      {item.visibility === 'listed' ? 'No Price' : 'Set price'}
                    </span>
                  )}
                </InlinePriceEditor>
              )}

              {item.visibility !== 'listed' && !isSelectionMode && !isSold && (
                <QuickListPopover item={item}>
                  <button
                    type="button"
                    onClick={(e) => e.stopPropagation()}
                    title="List for sale"
                    className="hidden md:inline-flex items-center gap-1 text-[9px] lg:text-[10px] px-1.5 py-0.5 rounded-full border border-active-gold/40 bg-active-gold/10 text-active-gold uppercase tracking-wide whitespace-nowrap hover:bg-active-gold/20 hover:border-active-gold/60 transition-colors"
                  >
                    <Tag className="w-2.5 h-2.5" />
                    List
                  </button>
                </QuickListPopover>
              )}
              {item.visibility !== 'listed' && isSelectionMode && !isSold && (
                <span className="text-[9px] lg:text-[10px] px-1.5 py-0.5 rounded-full border border-white/15 bg-white/[0.04] text-muted-foreground uppercase tracking-wide whitespace-nowrap">
                  Unlisted
                </span>
              )}
              
            </div>

            <button
              type="button"
              className="min-w-0 text-left cursor-pointer"
              onClick={handleContentClick}
              aria-label={`Manage ${item.title}`}
            >
              <ItemMetaBadges
                category={item.category}
                condition={item.condition}
                gradingCompany={item.grading_company}
                slabGrade={item.slab_grade}
                className={cn(dimText && 'opacity-60')}
              />
            </button>
          </div>
        </div>
      </div>

      <QuickPriceEditDialog
        open={priceEditOpen}
        onClose={() => setPriceEditOpen(false)}
        item={item}
      />

      <ImageSelectionDialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        item={item}
        hasExistingImages={hasUserImage}
      />
    </>
  );
}

export const CollectionItemCard = memo(CollectionItemCardImpl);
